// client/src/intermission/LeaderboardOverlay.js
import Phaser from 'phaser';

// Local helper to truncate wallet addresses (kept here to avoid cross-file imports)
function truncateWallet(addr) {
  try {
    if (!addr || typeof addr !== 'string') return 'Unknown';
    if (addr.length < 8) return addr;
    const first = addr.substring(0, 3);
    const last = addr.substring(addr.length - 4);
    return `${first}...${last}`;
  } catch (_) {
    return 'Unknown';
  }
}

export class LeaderboardOverlay {
  constructor(scene) {
    this.scene = scene;
    this.container = null;
    this.timerText = null;
    this.leaderboardItems = [];
    this.isVisible = false;
    this.joinScreenContainer = null;
    this.joinScreenItems = [];
  }

  show(playerRankings) {
    if (this.isVisible) return;
    this.isVisible = true;

    // Create semi-transparent background
    const uiW = (this.scene.uiCamera && this.scene.uiCamera.width) ? this.scene.uiCamera.width : 1440;
    const uiH = (this.scene.uiCamera && this.scene.uiCamera.height) ? this.scene.uiCamera.height : 900;
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x000000, 0.8);
    bg.fillRect(0, 0, uiW, uiH);
    bg.setDepth(2500);

    // Create container for the leaderboard
    this.container = this.scene.add.container(uiW / 2, uiH / 2);
    this.container.setDepth(2501);

    // Position background relative to the container so it covers the full UI viewport
    bg.setPosition(-uiW / 2, -uiH / 2);

    // Title
    const title = this.scene.add.text(0, -380, '🏆 BATTLE LEADERBOARD 🏆', {
      fontSize: '48px',
      fontFamily: 'Impact, Arial Black, sans-serif',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 6
    }).setOrigin(0.5).setDepth(2502);

    // Subtitle (show fewer players and larger font for readability)
    const subtitle = this.scene.add.text(0, -330, 'Top 10 Players', {
      fontSize: '30px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5).setDepth(2502);

    // Timer text
    this.timerText = this.scene.add.text(0, 420, 'Next battle in: 30', {
      fontSize: '28px',
      fontFamily: 'Arial, sans-serif',
      color: '#00FF00',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5).setDepth(2502);

    this.container.add([bg, title, subtitle, this.timerText]);

    // Create leaderboard entries
    this.createLeaderboardEntries(playerRankings);

    // Render on UI camera only: ignore on world camera
    if (this.scene.worldCamera) {
      [bg, title, subtitle, this.timerText, ...this.leaderboardItems].forEach(item => {
        this.scene.worldCamera.ignore(item);
      });
    }
  }

  createLeaderboardEntries(playerRankings) {
    const top10 = playerRankings.slice(0, 10);
    const startY = -280;
    const spacing = 44; // larger spacing to accommodate bigger fonts

    top10.forEach((ranking, index) => {
      const y = startY + (index * spacing);
      const rank = ranking.finalPosition;
      const player = ranking.player;
      const eliminations = ranking.eliminations;

      // Rank number
      const rankText = this.scene.add.text(-400, y, `#${rank}`, {
        fontSize: '30px',
        fontFamily: 'Arial, sans-serif',
        color: rank === 1 ? '#FFD700' : rank <= 3 ? '#C0C0C0' : '#FFFFFF',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0, 0.5).setDepth(2502);

      // Player name (truncated if too long)
      const displayName = player.name.length > 20 ? player.name.substring(0, 17) + '...' : player.name;
      const nameText = this.scene.add.text(-300, y, displayName, {
        fontSize: '28px',
        fontFamily: 'Arial, sans-serif',
        color: ranking.isWinner ? '#00FF00' : '#FFFFFF',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0, 0.5).setDepth(2502);

      // Eliminations count
      const elimText = this.scene.add.text(200, y, `${eliminations} elim${eliminations !== 1 ? 's' : ''}`, {
        fontSize: '26px',
        fontFamily: 'Arial, sans-serif',
        color: '#FFA500',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0, 0.5).setDepth(2502);

      // Winner crown emoji
      let crownText = null;
      if (ranking.isWinner) {
        crownText = this.scene.add.text(-450, y, '👑', {
          fontSize: '28px',
          fontFamily: 'Arial, sans-serif'
        }).setOrigin(0, 0.5).setDepth(2502);
      }

      // Status indicator (survivor or eliminated)
      const statusText = this.scene.add.text(350, y, ranking.survived ? '🏆' : '💀', {
        fontSize: '26px',
        fontFamily: 'Arial, sans-serif'
      }).setOrigin(0, 0.5).setDepth(2502);

      // Add to container and tracking array
      const items = [rankText, nameText, elimText, statusText];
      if (crownText) items.push(crownText);

      this.container.add(items);
      this.leaderboardItems.push(...items);
    });
  }

  updateTimer(secondsLeft) {
    if (this.timerText && this.isVisible) {
      this.timerText.setText(`Next battle in: ${secondsLeft}`);
    }
  }

  async showOnJoinScreen(playerRankings) {
    // Hide any existing join screen leaderboard first
    this.hideJoinScreen();

    console.log('[LeaderboardOverlay] showOnJoinScreen');

    // Create compact leaderboard for join screen (right side)
    const cameraWidth = this.scene.uiCamera.width;
    const cameraHeight = this.scene.uiCamera.height;
    const margin = 40;

    // Wider panel on the right side of the join overlay
    const panelWidth = 400;
    const leaderboardX = cameraWidth - margin - (panelWidth / 2);
    const leaderboardY = margin + 80;
    const panelHeight = cameraHeight - (margin * 2) - 280;

    // Background panel
    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(0x111111, 0.9);
    panelBg.fillRect(leaderboardX - panelWidth/2, leaderboardY, panelWidth, panelHeight);
    panelBg.setDepth(2002); // Above main overlay but below text
    this.scene.worldCamera.ignore(panelBg);
    this.joinScreenItems.push(panelBg);

    // Game Mode Display
    /*const gameModeInfo = this.scene.gameModeManager?.getCurrentMode();
    console.log('[LeaderboardOverlay] Join screen game mode:', { modeId: gameModeInfo?.modeId, name: gameModeInfo?.mode?.name });
    const gameModeText = gameModeInfo ? gameModeInfo.mode.name : 'Unknown Mode';
    const gameModeDisplay = this.scene.add.text(leaderboardX, leaderboardY + 20, `🎮 ${gameModeText}`, {
      fontSize: '22px',
      fontFamily: 'Impact, Arial Black, sans-serif',
      color: '#00FF00',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5).setDepth(2003);
    this.scene.worldCamera.ignore(gameModeDisplay);
    this.joinScreenItems.push(gameModeDisplay);
    */

    // Title
    const title = this.scene.add.text(leaderboardX, leaderboardY + 30, '🏆 TOP PLAYERS', {
      fontSize: '40px',
      fontFamily: 'Impact, Arial Black, sans-serif',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5).setDepth(2003);
    this.scene.worldCamera.ignore(title);
    this.joinScreenItems.push(title);

    // Build rows: always fetch from database leaderboard (top N points)
    let rows = [];
    try {
      const resp = await fetch('/api/battles/leaderboard/points?limit=10', {
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        }
      });
      const data = await resp.json();
      if (data && data.success && Array.isArray(data.data)) {
        rows = data.data.map((u, i) => ({
          rank: i + 1,
          name: (u.username && String(u.username).trim()) ? String(u.username) : truncateWallet(u.solana_wallet_address),
          stat: parseInt(u.total_points, 10) || 0
        }));
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }

    const startY = leaderboardY + 94; // Adjusted for game mode display
    const spacing = 40; // increased spacing for larger fonts

    rows.forEach((row, index) => {
      const y = startY + (index * spacing);

      const rankText = this.scene.add.text(leaderboardX - panelWidth/2 + 15, y, `#${row.rank}`, {
        fontSize: '30 px',
        fontFamily: 'Arial, sans-serif',
        color: row.rank === 1 ? '#FFD700' : row.rank <= 3 ? '#C0C0C0' : '#FFFFFF',
        stroke: '#000000',
        strokeThickness: 2
      }).setOrigin(0, 0.5).setDepth(2003);
      this.scene.worldCamera.ignore(rankText);
      this.joinScreenItems.push(rankText);

      const displayName = (row.name && row.name.length >= 12) ? (row.name.substring(0, 12) + '...') : row.name;
      const nameText = this.scene.add.text(leaderboardX - panelWidth/2 + 55, y, displayName, {
        fontSize: '30px',
        fontFamily: 'Arial, sans-serif',
        color: '#FFFFFF',
        stroke: '#000000',
        strokeThickness: 2
      }).setOrigin(0, 0.5).setDepth(2003);
      this.scene.worldCamera.ignore(nameText);
      this.joinScreenItems.push(nameText);

      // Points (or eliminations on fallback)
      const statText = this.scene.add.text(leaderboardX + panelWidth/2 - 25, y, `${row.stat}`, {
        fontSize: '30px',
        fontFamily: 'Arial, sans-serif',
        color: '#FFA500',
        stroke: '#000000',
        strokeThickness: 2
      }).setOrigin(1, 0.5).setDepth(2003);
      this.scene.worldCamera.ignore(statText);
      this.joinScreenItems.push(statText);
    });
  }

  hide() {
    if (!this.isVisible) return;

    this.isVisible = false;

    // Destroy all elements
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }

    this.leaderboardItems.forEach(item => {
      if (item && item.destroy) item.destroy();
    });
    this.leaderboardItems = [];

    if (this.timerText && this.timerText.destroy) {
      this.timerText.destroy();
      this.timerText = null;
    }
  }

  hideJoinScreen() {
    // Destroy join screen leaderboard elements
    this.joinScreenItems.forEach(item => {
      if (item && item.destroy) item.destroy();
    });
    this.joinScreenItems = [];
  }

  destroy() {
    this.hide();
  }
}
